// @ts-nocheck
import { useState } from "react";
import { useLocation } from "wouter";
import { useListDevices, useCreateDevice, useDeleteDevice, getListDevicesQueryKey } from "../lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useLang } from "../lib/lang";

export function Devices() {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const { t } = useLang();
  const d = t.devices;

  const { data: devices, isLoading } = useListDevices();
  const createDevice = useCreateDevice();
  const deleteDevice = useDeleteDevice();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [formError, setFormError] = useState("");

  const total = devices?.length ?? 0;
  const active = devices?.filter((dev) => dev.status === "connected").length ?? 0;
  const inactive = total - active;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    try {
      const API_BASE = import.meta.env.VITE_API_URL ?? "";
      const res = await fetch(`${API_BASE}/api/devices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, phoneNumber, webhookUrl: webhookUrl || undefined }),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.error ?? "Failed to create device"); return; }
      setName(""); setPhoneNumber(""); setWebhookUrl(""); setShowForm(false);
      qc.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      navigate(`/devices/${data.id}/qr`);
    } catch (err: any) {
      setFormError(err?.message ?? "Failed to create device. Please try again.");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(d.deleteConfirm)) return;
    await deleteDevice.mutateAsync({ id });
    qc.invalidateQueries({ queryKey: getListDevicesQueryKey() });
  };

  const statusInfo = (status: string) => {
    if (status === "connected") return { bg: "bg-[#d4edda] text-[#155724]", dot: "text-[#2dce89]", label: d.connected };
    if (status === "connecting") return { bg: "bg-[#fff3cd] text-[#856404]", dot: "text-[#fb6340]", label: d.connecting };
    return { bg: "bg-[#fde8ec] text-[#721c24]", dot: "text-[#f5365c]", label: d.pending };
  };

  return (
    <div>
      <PageHeader title={d.title} actions={[{ label: d.createDevice, icon: "fas fa-plus", onClick: () => setShowForm(true) }]} />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={d.totalDevices} value={total} iconClass="fas fa-server" gradient="primary" />
          <StatCard label={d.activeDevices} value={active} iconClass="fas fa-check-circle" gradient="success" />
          <StatCard label={d.inactiveDevices} value={inactive} iconClass="fas fa-exclamation-circle" gradient="warning" />
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <h5 className="font-semibold text-[#32325d]">{d.createNew}</h5>
                <button onClick={() => setShowForm(false)} className="text-[#8898aa] hover:text-[#32325d] w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f6f9fc]">
                  <i className="fas fa-times" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                {formError && (
                  <div className="bg-[#f8d7da] border border-[#f5c6cb] text-[#721c24] px-3 py-2 rounded-lg text-xs flex items-start gap-2">
                    <i className="fas fa-exclamation-circle mt-0.5 shrink-0" />{formError}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.deviceName}</label>
                  <input value={name} onChange={e => setName(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder={d.deviceNamePlaceholder} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.phoneNumber}</label>
                  <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder={d.phonePlaceholder} type="tel" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.webhookUrl} <span className="text-[#adb5bd] font-normal">({t.common.optional})</span></label>
                  <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder="https://your-webhook-url.com" type="url" />
                  <p className="text-xs text-[#8898aa] mt-1">{d.webhookHint}</p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f] hover:bg-[#f6f9fc]">{t.common.cancel}</button>
                  <button type="submit" disabled={createDevice.isPending} className="px-4 py-2 text-sm gradient-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50">
                    {createDevice.isPending ? t.common.creating : t.common.create}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-44 bg-white rounded-xl shadow-sm border border-[#e9ecef] animate-pulse" />)}
          </div>
        ) : (devices?.length ?? 0) === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-12 text-center">
            <i className="fas fa-mobile-alt text-5xl text-[#cad1d7] mb-4 block" />
            <h5 className="text-[#32325d] font-semibold mb-2">{d.noDevices}</h5>
            <p className="text-[#8898aa] text-sm mb-5">{d.noDevicesHint}</p>
            <button onClick={() => setShowForm(true)} className="gradient-primary text-white px-5 py-2 rounded-lg text-sm shadow-sm">
              <i className="fas fa-plus me-2" />{d.createDevice}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {devices?.map((device) => {
              const s = statusInfo(device.status);
              const isConnected = device.status === "connected";
              return (
                <div key={device.id} className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5 flex flex-col">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isConnected ? "gradient-success" : "bg-[#f6f9fc]"}`}>
                      <i className={`fab fa-whatsapp text-lg ${isConnected ? "text-white" : "text-[#cad1d7]"}`} />
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#32325d] text-sm leading-tight">{device.name}</h5>
                      {device.phoneNumber && <p className="text-xs text-[#8898aa] mt-0.5">{device.phoneNumber}</p>}
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${s.bg}`}>
                      <i className={`fas fa-circle text-[7px] ${s.dot}`} />{s.label}
                    </span>
                  </div>
                  {device.lastSeen ? (
                    <p className="text-xs text-[#8898aa] mb-4 flex items-center gap-1">
                      <i className="fas fa-clock text-[10px]" />{d.lastSeen} {new Date(device.lastSeen).toLocaleString()}
                    </p>
                  ) : (
                    <p className="text-xs text-[#cad1d7] mb-4 italic">{d.neverConnected}</p>
                  )}
                  <div className="flex items-center gap-2 mt-auto">
                    <button
                      onClick={() => navigate(`/devices/${device.id}/qr`)}
                      className={`flex-1 text-center text-xs font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 ${isConnected ? "border border-[#e9ecef] text-[#8898aa] hover:bg-[#f6f9fc]" : "gradient-primary text-white shadow-sm hover:opacity-90"}`}
                    >
                      <i className="fas fa-qrcode" />{isConnected ? d.relinkQr : d.scanQr}
                    </button>
                    <button onClick={() => handleDelete(device.id)} className="text-xs font-medium py-2 px-3 border border-[#e9ecef] rounded-lg text-[#f5365c] hover:bg-red-50 hover:border-red-200 transition-colors">
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
